import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { AUTH_EVENTS, type AuthRefreshedDetail } from '../api';
import { postLogout, postRefresh } from './authClient';
import { withRefreshLock } from './refreshLock';
import { getAccessToken, setAccessToken } from './tokenStore';
import type { AuthStatus, AuthUser } from './types';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  isAuthenticated: boolean;
  /** POST /auth/logout, clear in-memory token, flip to anonymous. Safe to
   *  call when already anonymous (no-ops). */
  logout: () => Promise<void>;
  /** Force a /auth/refresh and update user + token. Useful after profile
   *  edits land via a future endpoint. */
  refresh: () => Promise<void>;
  /** Apply a fresh AuthUser to in-memory state synchronously, without doing
   *  a /auth/refresh round-trip. The access token is unchanged (it carries
   *  only userId + email — `status` is not encoded), so this is safe to use
   *  after an endpoint that mutates the user record and returns the updated
   *  shape (e.g. POST /auth/register transitioning PENDING → ACTIVE).
   *  Always flips status to 'authenticated' since you can't have a user
   *  object without an authenticated session. */
  setUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// The non-httpOnly session indicator cookie set by the API alongside the
// httpOnly refreshToken (see api/src/modules/auth/auth.controller.ts:29).
// Its only job is to tell the frontend "a refresh might succeed — try it"
// without exposing the actual token to JS.
const SESSION_INDICATOR_COOKIE = 'gh_session';

function hasSessionIndicator(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split('; ').some((c) => c.startsWith(`${SESSION_INDICATOR_COOKIE}=`));
}

function clearSessionIndicator(): void {
  if (typeof document === 'undefined') return;
  // Match the path used when the cookie was set so the browser actually
  // expires it. Secure/sameSite don't need to match on deletion.
  document.cookie = `${SESSION_INDICATOR_COOKIE}=; Path=/; Max-Age=0`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(() =>
    hasSessionIndicator() ? 'loading' : 'anonymous',
  );
  const [user, setUser] = useState<AuthUser | null>(null);

  // Guard so the boot-time refresh fires exactly once even under StrictMode
  // double-invocation of effects.
  const bootedRef = useRef(false);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    if (!hasSessionIndicator()) {
      setStatus('anonymous');
      return;
    }

    // No `cancelled` flag here. Earlier this effect captured a per-invocation
    // `cancelled` boolean and flipped it true on cleanup, which interacted
    // badly with React 18's StrictMode double-invocation in dev:
    //   1. Pass 1 sets `bootedRef.current = true`, fires the refresh.
    //   2. Cleanup runs → that closure's `cancelled = true`.
    //   3. Pass 2 sees `bootedRef.current` is true → returns early, no new
    //      refresh.
    //   4. The first refresh resolves → `.then` sees `cancelled = true` →
    //      bails without ever calling setStatus. Status is stuck on
    //      'loading' forever → AppBar reads "Checking session…" until the
    //      tab is closed.
    // The AuthProvider wraps the whole app and never unmounts in practice,
    // so dropping the cancellation is safe. React 18 also no longer warns
    // about setState after unmount, so even the edge case is benign.
    void withRefreshLock(async () => postRefresh())
      .then((res) => {
        setAccessToken(res.data.tokens.accessToken);
        setUser(res.data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        setAccessToken(null);
        clearSessionIndicator();
        setUser(null);
        setStatus('anonymous');
      });
  }, []);

  // React to refresh events triggered by apiFetch's 401 interceptor (e.g.
  // a leaderboard /me call refreshes the session — the user object can
  // shift if the API updates it, and the AuthContext needs to see that).
  useEffect(() => {
    function onRefreshed(e: Event): void {
      const detail = (e as CustomEvent<AuthRefreshedDetail>).detail;
      if (!detail) return;
      setUser(detail.user);
      setStatus('authenticated');
    }
    function onCleared(): void {
      setUser(null);
      setStatus('anonymous');
      clearSessionIndicator();
    }
    window.addEventListener(AUTH_EVENTS.refreshed, onRefreshed);
    window.addEventListener(AUTH_EVENTS.cleared, onCleared);
    return () => {
      window.removeEventListener(AUTH_EVENTS.refreshed, onRefreshed);
      window.removeEventListener(AUTH_EVENTS.cleared, onCleared);
    };
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const token = getAccessToken();
    if (token) {
      await postLogout(token);
    }
    setAccessToken(null);
    clearSessionIndicator();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await withRefreshLock(async () => postRefresh());
      setAccessToken(res.data.tokens.accessToken);
      setUser(res.data.user);
      setStatus('authenticated');
    } catch {
      setAccessToken(null);
      clearSessionIndicator();
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const applyUser = useCallback((nextUser: AuthUser): void => {
    setUser(nextUser);
    setStatus('authenticated');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === 'authenticated',
      logout,
      refresh,
      setUser: applyUser,
    }),
    [status, user, logout, refresh, applyUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() must be used inside <AuthProvider>');
  return ctx;
}
